import type { FieldWhere, WhereClause, WhereLeaf } from '../../../query-dsl';
import type { ColumnFilter } from '../types';
import { nestCustomFieldWhere, type CustomFieldFilter } from './custom-field-column';
import { nestWhereCondition } from './nested-column';

/**
 * Maps a UI filter operator + value to the corresponding Aurora Query DSL
 * leaf comparison. Text operators wrap the value in the right `iLike`
 * wildcards; comparison operators pass the value through. An unknown
 * operator falls back to a bare `iLike` on the raw value (legacy behavior,
 * preserved 1:1).
 */
export function toFilterLeaf(operator: string, value: string): WhereLeaf {
  switch (operator) {
    case 'contains':
      return { iLike: `%${value}%` };
    case 'startsWith':
      return { iLike: `${value}%` };
    case 'endsWith':
      return { iLike: `%${value}` };
    case 'equals':
      return { eq: value };
    case 'greaterThan':
      return { gt: value };
    case 'lessThan':
      return { lt: value };
    default:
      return { iLike: value };
  }
}

/**
 * Converts ColumnFilter[] from the TableFilters into an Aurora Query DSL
 * `where` clause with bare operator keys.
 *
 * Multiple filters are combined with a bare `and`; a single filter is
 * returned unwrapped. Filter columns using dot notation
 * (e.g. `'boundedContext.name'`) are translated to the DSL's nested relation
 * object (EXISTS filter) automatically.
 *
 * An entry MAY also be a `CustomFieldFilter` descriptor — a SYNTHETIC
 * custom-field column filter merged in at runtime (design.md D4). Unlike a
 * plain `ColumnFilter` (whose operator comes from the generic UI vocabulary,
 * see `toFilterLeaf`), a `CustomFieldFilter` carries the exact DSL leaf
 * condition directly (`eq`/`in` on `valueOptionId` for `select`,
 * `contains`/`overlap` on `valueOptionIds` for `multiselect`) and compiles
 * to an EXISTS predicate nested under `customFieldValues`.
 *
 * @returns A DSL where clause, or undefined if no filters
 */
export function buildFilterWhere(
  filters: Array<ColumnFilter | CustomFieldFilter>,
): WhereClause | undefined {
  if (!filters.length) return undefined;

  const conditions = filters.map(buildCondition);

  if (conditions.length === 1) return conditions[0];

  return { and: conditions };
}

function isCustomFieldFilter(
  filter: ColumnFilter | CustomFieldFilter,
): filter is CustomFieldFilter {
  return (filter as CustomFieldFilter).kind === 'customField';
}

function buildCondition(filter: ColumnFilter | CustomFieldFilter): FieldWhere {
  if (isCustomFieldFilter(filter)) {
    return nestCustomFieldWhere(filter.definitionId, filter.valueColumn, filter.condition);
  }

  return nestWhereCondition(filter.column, toFilterLeaf(filter.operator, filter.value));
}
